import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  HStack,
  Input,
  Select,
  Stack,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { useFetch } from 'use-http';
import { api, makeHeaders } from '../../../util/api';
import Loading from '../../Misc/Loading';
import { ERROR_MESSAGE, useNotify } from '../../../hooks/useNotify';
import { useNavigate, useParams } from 'react-router';
import { ElectoralSection, Question, QuestionType, Survey, SurveyAnswer, SurveyConfig, SurveyConfigDisplay, SurveyRespondentInfo, SurveyResponse } from '../types';
import { getSurveyConfig } from '../../../util/array';
import TextLabel from '../../Misc/LText';
import ComboBox from '../../Misc/Combobox';
import TextQuestion from './TextQuestion';
import BooleanQuestion from './BooleanQuestion';
import MultipleQuestion from './MultipleQuestion';
import RangeQuestion from './RangeQuestion';
import { formatDate, maxLengthHandler } from '../../../util/string';

const SurveyForm: FC = () => {
  const notify = useNotify();
  const { id } = useParams();
  const [survey, setSurvey] = useState<null | Survey>(null);
  const [config, setConfig] = useState<SurveyConfig | null>(null);
  const [sections, setSections] = useState<string[]>([]);
  const [respondentData, setRespondentData] = useState<SurveyConfigDisplay[]>([]);
  const navigate = useNavigate();

  const allQuestionAnswered = useMemo(() => {
    if (!survey) return false;
    return survey.content.questions.every((question) => {
      if (Array.isArray(question.response)) {
        return question.response.length
      } else {
        return question.response
      }
    });
  }, [survey]);

  const {
    error,
    cache,
    loading,
    response,
    get,
  } = useFetch(`${api}/surveys`, makeHeaders());

  const {
    error: configError,
    cache: configCache,
    loading: configLoading,
    response: configResponse,
    get: getConfig,
  } = useFetch(`${api}/admin/config`, makeHeaders());

  const {
    error: sectionsError,
    cache: sectionsCache,
    loading: sectionsLoading,
    response: sectionsResponse,
    get: getSections,
  } = useFetch(`${api}/sections`, makeHeaders());

  const {
    error: responseError,
    cache: responseCache,
    loading: responseLoading,
    response: responseRes,
    post: postResponse,
  } = useFetch(`${api}/surveys/${survey?.id}/responses`, makeHeaders());

  useEffect(() => {
    if (config) {
      const respondentData = getSurveyConfig(config);
      setRespondentData(respondentData);
    }
  }, [config])

  const refreshSurvey = async () => {
    cache.clear();
    const survey = await get(`/${id}`);
    if (!error && response.ok && survey) {
      setSurvey(survey);
    } else {
      notify(ERROR_MESSAGE, 'error');
    }
  };

  const refreshConfig = async () => {
    configCache.clear();
    const newConfig = await getConfig();
    if (!configError && configResponse.ok && newConfig) {
      setConfig(newConfig);
    } else {
      notify(ERROR_MESSAGE, 'error');
    }
  };

  const refreshSections = async () => {
    sectionsCache.clear();
    const items: ElectoralSection[] = await getSections();
    if (!sectionsError && sectionsResponse.ok && items) {
      const sectionValues = items.map((section) => section.sectionNumber.toString());
      setSections(sectionValues);
    } else {
      notify(ERROR_MESSAGE, 'error');
    }
  };

  useEffect(() => {
    refreshSurvey();
    refreshConfig();
    refreshSections();
  }, []); // eslint-disable-line

  const onChange = (newValue: string, item: SurveyConfigDisplay) => {
    const draft = [...respondentData];
    const index = draft.findIndex((x) => x.value === item.value);
    draft[index].response = newValue;
    setRespondentData(draft);
  };

  const updateResponse = (response: number | string | string[], question: Question) => {
    if (!survey) return;
    const draft = { ...survey };
    const index = draft.content.questions.findIndex((item) => item.id === question.id);
    draft.content.questions[index].response = response;
    setSurvey(draft);
  };

  const renderQuestion = (question: Question) => {
    switch (question.questionType) {
      case QuestionType.TEXT:
        return <TextQuestion question={question} onChange={updateResponse} />;
      case QuestionType.BOOLEAN:
        return <BooleanQuestion question={question} onChange={updateResponse} />;
      case QuestionType.MULTIPLE:
        return <MultipleQuestion question={question} onChange={updateResponse} />;
      case QuestionType.RANGE_SCALE:
        return <RangeQuestion question={question} onChange={updateResponse} />;
      default:
        return null;
    }
  };

  const disableSave = useMemo(() => {
    if (!survey) return true;
    return !allQuestionAnswered || respondentData.some((item) => !item.response)
  }, [survey, allQuestionAnswered, respondentData]);

  const handleSaveResponse = async () => {
    if (!survey) return;

    const respondentInfo = respondentData.reduce((acc: Partial<SurveyResponse>, item) => {
      acc[item.value as keyof SurveyRespondentInfo] = item.response as string;
      return acc;
    }, {});

    const answers = survey.content.questions.reduce((acc: SurveyAnswer, item) => {
      acc[item.id] = item.response;
      return acc;
    }, {});

    const body = {
      ...respondentInfo,
      answers,
    };

    try {
      responseCache.clear();
      await postResponse(body);

      if (responseError || !responseRes.ok) {
        throw new Error();
      }

      notify('Encuesta guardada correctamente', 'success');
      navigate(`/encuestas/${survey.id}`, {
        replace: true,
      });
    } catch (error) {
      notify(ERROR_MESSAGE, 'error');
    }
  };

  return (
    <Stack maxW='container.lg'>
      <Card px={8} py={4} variant='outline'>
        <Loading isLoading={loading} dense />
        <HStack gap={4}>
          <VStack align='start' w='full' gap={0}>
            <Text
              fontSize='3xl'
              fontWeight='semibold'
              mb={2}
            >
              {survey?.title}
            </Text>
            <Text fontSize='xs' color='GrayText'>
              Creada el {formatDate(survey?.createdAt)}
            </Text>
          </VStack>
        </HStack>
      </Card>

      <Card
        variant='outline'
        p={4}
      >
        <Text fontWeight='bold' color='gray.500' textTransform='uppercase' fontSize='xs' mb={3}>Datos del encuestado</Text>
        <Loading isLoading={configLoading} dense />
        <HStack>
          <VStack align='start' w='full' gap={0}>
            {respondentData.map((item) => (
              <VStack mb={5} width='full' align='start' key={item.value}>
                {item.type === 'section' && (
                  <>
                    <TextLabel title={item.label} />
                    <ComboBox
                      isDisabled={sectionsLoading}
                      placeholder='Selecciona una sección'
                      options={sections}
                      onChange={(value) => onChange(value, item)}
                      value={item.response as string}
                    />
                  </>
                )}
                {item.type === 'combo' && (
                  <>
                    <TextLabel title={item.label} />
                    <Select
                      value={item.response as string}
                      onChange={(e) => onChange(e.target.value, item)}
                    >
                      {item.options?.map((option: string, index) => (
                        <option key={index} value={option}>{option}</option>
                      ))}
                    </Select>
                  </>
                )}
                {item.type === 'text' && (
                  <>
                    <TextLabel title={item.label} />
                    <Input
                      value={item.response as string}
                      placeholder='Escribe tu respuesta'
                      onChange={(e) => onChange(e.target.value, item)}
                      type={item.type}
                      max={item.maxLength}
                      onInput={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (item.maxLength) {
                          const value = e.currentTarget.value;
                          e.currentTarget.value = maxLengthHandler(value, item.maxLength);
                        }
                      }}
                    />
                  </>
                )}
                {item.type === 'number' && (
                  <>
                    <TextLabel title={item.label} />
                    <Input
                      min={0}
                      value={item.response as string}
                      placeholder='Escribe tu respuesta'
                      onChange={(e) => onChange(e.target.value, item)}
                      type={item.type}
                      max={item.maxLength}
                      onInput={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (item.maxLength) {
                          const value = e.currentTarget.value;
                          e.currentTarget.value = maxLengthHandler(value, item.maxLength);
                        }
                      }}
                    />
                  </>
                )}
              </VStack>
            ))}
          </VStack>
        </HStack>
      </Card>

      <Card
        variant='outline'
        p={4}
      >
        <Text fontWeight='bold' color='gray.500' textTransform='uppercase' fontSize='xs' mb={3}>Preguntas de la encuesta</Text>

        <Loading isLoading={loading} dense />

        <HStack>
          <VStack align='start' w='full' gap={0}>
            {survey?.content.questions.map((question, index) => (
              <HStack
                mb={5}
                gap={3}
                width='full'
                align='start'
                key={question.id}
              >
                <Badge
                  colorScheme='brand'
                  variant='outline'
                  borderRadius='50%'
                  minW={5}
                  width={5}
                  height={5}
                  justifyContent='center'
                  alignItems='center'
                  display='flex'
                >
                  {index + 1}
                </Badge>
                {renderQuestion(question)}
              </HStack>
            ))}
          </VStack>
        </HStack>

      </Card>

      <Tooltip
        label={(
          !allQuestionAnswered ?
            'Debes responder todas las preguntas para poder guardar la encuesta' :
            ''
        )}
      >
        <Button
          colorScheme='brand'
          isDisabled={disableSave}
          onClick={handleSaveResponse}
          isLoading={responseLoading}
        >
          Guardar
        </Button>
      </Tooltip>
    </Stack>
  )
};

export default SurveyForm;
